import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { mcpCall } from '../lib/mcp';
import '../components/RecordsTable/RecordsTable.css';
import './Settings.css';
import './Privacy.css';

/**
 * Channel clicks (2026-09-28). The consumer site's /products page sends shoppers to Amazon, Walmart
 * and Target through its /go route, which records every click (Mike: "we definitely want to track
 * all of the clicks that go to those channels"). This is the count of those clicks by channel, MERV
 * tier, product and day, from retail_channels.get_channel_click_report.
 *
 * ONE CALL, FOUR TABLES. RecordsTable caches by tool and arguments, so four of them over one report
 * would all show whichever table's rows loaded first; the report is fetched once here instead.
 *
 * Bots (crawlers, requests with no user agent, browser prefetches) are excluded unless the box is
 * ticked; their count is always shown. No household or quiz session is shown anywhere.
 */

interface Totals {
  clicks: number;
  bots: number;
  anonymous: number;
  linked: number;
  mobile: number;
  desktop: number;
}
interface ByChannel {
  channel: string;
  channel_name: string;
  clicks: number;
}
interface ByMerv {
  merv: number;
  clicks: number;
}
interface PerChannel {
  amazon: number;
  walmart: number;
  target: number;
  clicks: number;
}
interface ByProduct extends PerChannel {
  sku_id: number;
  sku_code: string;
  size: string;
  merv: number;
}
interface ByDay extends PerChannel {
  day: string;
}
interface Report {
  totals: Totals;
  byChannel: ByChannel[];
  byMerv: ByMerv[];
  byProduct: ByProduct[];
  byDay: ByDay[];
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

function Table({ title, head, rows, empty }: { title: string; head: ReactNode; rows: ReactNode[]; empty: string }) {
  return (
    <section className="records" aria-label={title}>
      <header className="records__head">
        <div className="records__head-text">
          <h2 className="records__title">{title}</h2>
        </div>
      </header>
      <div className="records__panel">
        {rows.length === 0 ? (
          <div className="records__state">{empty}</div>
        ) : (
          <div className="records__scroll">
            <table className="records__table">
              <thead>{head}</thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

const num = (n: number) => <td className="records__td--num">{n.toLocaleString()}</td>;

export function ChannelClicks() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [includeBots, setIncludeBots] = useState(false);
  const inverted = DATE.test(from) && DATE.test(to) && from > to;
  const args: Record<string, unknown> = { includeBots };
  if (!inverted && DATE.test(from)) args.from = from;
  if (!inverted && DATE.test(to)) args.to = to;

  const report = useQuery({
    queryKey: ['mcp', 'getChannelClickReport', args],
    queryFn: () => mcpCall<Report>('getChannelClickReport', args),
  });
  const r = report.data;

  return (
    <>
      <div className="settings__list">
        <div className="settings__row">
          <div className="settings__meta">
            <p className="settings__key" id="range-title">Channel clicks</p>
            <p className="settings__desc">
              Clicks on the Amazon, Walmart and Target buttons on cabanafilters.com/products, by the day they
              happened (UTC). Leave both dates empty for all time.
            </p>
            {inverted && (
              <p className="privacy__error" role="alert">
                The start date is after the end date.
              </p>
            )}
          </div>
          <div className="privacy__range" role="group" aria-labelledby="range-title">
            <label className="privacy__field">
              From
              <input className="settings__input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="privacy__field">
              To
              <input className="settings__input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label className="privacy__field">
              <span>
                <input type="checkbox" checked={includeBots} onChange={(e) => setIncludeBots(e.target.checked)} /> Include bots
              </span>
            </label>
            <button type="button" className="records__refresh" onClick={() => report.refetch()} disabled={report.isFetching}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {report.isPending && (
        <div className="records__state" role="status" aria-live="polite">
          <span className="records__spinner" aria-hidden="true" /> Loading clicks…
        </div>
      )}
      {report.isError && (
        <div className="records__state records__state--error" role="alert">
          Could not load the click report. <span className="records__state-detail">{(report.error as Error).message}</span>
        </div>
      )}

      {r && (
        <>
          <dl className="privacy__counts" aria-label="Clicks in this range">
            <div className="privacy__count">
              <dt>{includeBots ? 'Clicks, bots included' : 'Clicks'}</dt>
              <dd>{r.totals.clicks}</dd>
            </div>
            <div className="privacy__count">
              <dt>Bots {includeBots ? '(included)' : '(excluded)'}</dt>
              <dd>{r.totals.bots}</dd>
            </div>
            <div className="privacy__count">
              <dt>Anonymous (rejected or GPC)</dt>
              <dd>{r.totals.anonymous}</dd>
            </div>
            <div className="privacy__count">
              <dt>Linked to an assessment</dt>
              <dd>{r.totals.linked}</dd>
            </div>
            <div className="privacy__count">
              <dt>Mobile / desktop</dt>
              <dd>
                {r.totals.mobile} / {r.totals.desktop}
              </dd>
            </div>
          </dl>

          <Table
            title="By channel"
            empty="No retail channels."
            head={
              <tr>
                <th>Channel</th>
                <th className="records__th--num">Clicks</th>
              </tr>
            }
            rows={r.byChannel.map((c) => (
              <tr key={c.channel}>
                <td>{c.channel_name}</td>
                {num(c.clicks)}
              </tr>
            ))}
          />

          <Table
            title="By MERV tier"
            empty="No clicks in this range."
            head={
              <tr>
                <th>Tier</th>
                <th className="records__th--num">Clicks</th>
              </tr>
            }
            rows={r.byMerv.map((m) => (
              <tr key={m.merv}>
                <td>MERV {m.merv}</td>
                {num(m.clicks)}
              </tr>
            ))}
          />

          <Table
            title="By product"
            empty="No clicks in this range."
            head={
              <tr>
                <th>SKU</th>
                <th>Size</th>
                <th>Tier</th>
                <th className="records__th--num">Amazon</th>
                <th className="records__th--num">Walmart</th>
                <th className="records__th--num">Target</th>
                <th className="records__th--num">Total</th>
              </tr>
            }
            rows={r.byProduct.map((p) => (
              <tr key={p.sku_id}>
                <td>
                  <code>{p.sku_code}</code>
                </td>
                <td>{p.size}</td>
                <td>MERV {p.merv}</td>
                {num(p.amazon)}
                {num(p.walmart)}
                {num(p.target)}
                {num(p.clicks)}
              </tr>
            ))}
          />

          <Table
            title="By day"
            empty="No clicks in this range."
            head={
              <tr>
                <th>Day (UTC)</th>
                <th className="records__th--num">Amazon</th>
                <th className="records__th--num">Walmart</th>
                <th className="records__th--num">Target</th>
                <th className="records__th--num">Total</th>
              </tr>
            }
            rows={r.byDay.map((d) => (
              <tr key={d.day}>
                <td>{d.day}</td>
                {num(d.amazon)}
                {num(d.walmart)}
                {num(d.target)}
                {num(d.clicks)}
              </tr>
            ))}
          />
        </>
      )}
    </>
  );
}
